import * as React from 'react';
import { useState, useContext, createContext, useEffect } from 'react';
import { usersApi } from '../services/users';
import { User } from '../models/users/user';
import { analyticsApi } from '../services/analytics';
import { setAmplitudeUserId } from '../services/amplitude';

interface IAuthContext {
  user?: User;
  isAuthenticated: boolean;
  isLoading: boolean;

  signIn: (email: string, password: string) => Promise<void | {message: string, error: boolean}>;
  signUp: (email: string, password: string) => Promise<void | {message: string, error: boolean}>;
  signOut: () => Promise<void>;
}

export interface IUserStorage {
  accessToken: null;
  user: User;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  return useContext(AuthContext);
};

function useProvideAuth() {
  const [user, setUser] = useState<User>();
  const [accessToken, setAccessToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    loadUser();
  }, []);

  const signIn = async (email: string, password: string): Promise<void | {message: string, error: boolean}> => {
    setIsLoading(true);
    try {
      const response = await usersApi.login(email, password);
      if (response.data.accessToken) {
        asyncLocalStorage
          .setItem(
            'userData',
            JSON.stringify({
              accessToken: response.data.accessToken,
              user: response.data.user,
            }),
          )
          .then(() => {
            setUser(new User(response.data.user));
            setAccessToken(response.data.accessToken);
            setIsAuthenticated(true);
            setAmplitudeUserId(response.data.user._id);
          });
        analyticsApi.logEvent(
          response.data.accessToken,
          'Action',
          {},
          'Sign In Success',
          { email }
        );
      }
    } catch (error) {
      if (error.response.data.error) {
        analyticsApi.logEvent(
          '',
          'Action',
          { message: error.response.data.message },
          'Sign In Error'
        )
        return error.response.data
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadUser = async () => {
    asyncLocalStorage.getItem('userData').then((storage) => {
      if (storage) {
        const userData = JSON.parse(storage) as IUserStorage;
        console.log(userData)
        setUser(new User(userData.user));
        setAccessToken(userData.accessToken);
        setIsAuthenticated(true);
        setAmplitudeUserId(userData.user._id);
      }

      setIsLoading(false);
    });
  };

  const signUp = async (email: string, password: string): Promise<void | {message: string, error: boolean}> => {
    setIsLoading(true);
    try {
      const response = await usersApi.register(email, password);
      if (response.data.accessToken) {
        asyncLocalStorage
          .setItem(
            'userData',
            JSON.stringify({
              accessToken: response.data.accessToken,
              user: response.data.user,
            }),
          )
          .then(() => {
            setUser(new User(response.data.user));
            setAccessToken(response.data.accessToken);
            setIsAuthenticated(true);
            setAmplitudeUserId(response.data.user._id);
          });
        analyticsApi.logEvent(
          response.data.accessToken,
          'Action',
          {},
          'Sign Up Success',
          { email }
        );
      }
    } catch (error) {
      if (error.response.data.error) {
        analyticsApi.logEvent(
          '',
          'Action',
          { message: error.response.data.message },
          'Sign Up Error'
        )
        return error.response.data
      }
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    localStorage.removeItem('userData');
    window.location.href = '/';
  };

  return {
    user,
    isAuthenticated,
    isLoading,

    signIn,
    signUp,
    signOut,
  };
}

const asyncLocalStorage = {
  setItem: function (key, value) {
    return Promise.resolve().then(function () {
      localStorage.setItem(key, value);
    });
  },

  getItem: function (key) {
    return Promise.resolve().then(function () {
      return localStorage.getItem(key);
    });
  },
};
